import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ArenaModal.css';
import CardHand from './CardHand';

// Spell type display (reuse from CombatInterface)
const SPELL_TYPE_INFO = {
  DAMAGE: { label: 'Damage', color: '#e74c3c', icon: '✨' },
  HEAL: { label: 'Heal', color: '#27ae60', icon: '✨' },
  BUFF_ATT: { label: 'Buff Attack', color: '#f39c12', icon: '✨' },
  BUFF_DEF: { label: 'Buff Defense', color: '#3498db', icon: '✨' },
  DEBUFF_DEF: { label: 'Debuff Def', color: '#8e44ad', icon: '✨' },
  DEBUFF_ATT: { label: 'Debuff Att', color: '#c0392b', icon: '✨' },
  DEBUFF_MISS: { label: 'Miss Chance', color: '#7f8c8d', icon: '✨' },
  HP_DIVIDE: { label: 'HP Divide', color: '#2c3e50', icon: '✨' },
  MIRROR_IMAGE: { label: 'Mirror Image', color: '#1abc9c', icon: '✨' },
  DIVINATION: { label: 'Divination', color: '#f1c40f', icon: '✨' },
  DRAIN: { label: 'Drain', color: '#8e44ad', icon: '✨' },
  COMBO_DAMAGE: { label: 'Combo', color: '#e67e22', icon: '✨' },
  COMBO_BUFF: { label: 'Combo Buff', color: '#2ecc71', icon: '✨' },
  COMBO_DEBUFF: { label: 'Combo Debuff', color: '#e74c3c', icon: '✨' },
  STAT_UP: { label: 'Stat Up', color: '#3498db', icon: '✨' },
  STAT_DOWN: { label: 'Stat Down', color: '#e74c3c', icon: '✨' },
};

const ArenaModal = ({ character, socket, onClose }) => {
  // Mode: 'lobby' or 'battle' 
  const [mode, setMode] = useState('lobby');

  // Lobby state 
  const [tab, setTab] = useState('challenges'); // challenges | leaderboard | active
  const [challenges, setChallenges] = useState([]);
  const [activeFights, setActiveFights] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [myRecord, setMyRecord] = useState({ wins: 0, losses: 0, draws: 0 });
  const [myChallengeOpen, setMyChallengeOpen] = useState(false);
  const [message, setMessage] = useState('');

  // ⚔️ Battle state 
  const [battle, setBattle] = useState(null);
  const [actionMode, setActionMode] = useState('main');
  const [battleResult, setBattleResult] = useState(null);

  const logRef = useRef(null);

  // Socket listeners 
  useEffect(() => {
    if (!socket) return;

    // Request current arena state
    socket.emit('arena_enter');

    const onArenaState = (data) => {
      setChallenges(data.challenges || []);
      setActiveFights(data.activeFights || []);
      setLeaderboard(data.leaderboard || []);
      setOnlinePlayers(data.onlinePlayers || []);
      setMyRecord(data.myRecord || { wins: 0, losses: 0, draws: 0 });

      // Check if already in a battle
      if (data.inBattle) {
        setMode('battle');
      }
    };

    const onLobbyUpdate = (data) => {
      setChallenges(data.challenges || []);
      setActiveFights(data.activeFights || []);
    };

    const onChallengeCreated = (data) => {
      setMyChallengeOpen(true);
      setMessage('Challenge posted! Waiting for an opponent...');
    };

    const onChallengeCancelled = () => {
      setMyChallengeOpen(false);
      setMessage('Challenge cancelled.');
    };

    const onBattleStarted = (data) => {
      setMode('battle');
      setBattle(data);
      setBattleResult(null);
      setActionMode('main');
      setMyChallengeOpen(false);
      setMessage('');
    };

    const onBattleUpdate = (data) => {
      setBattle(data);
      setActionMode('main');
    };

    const onBattleEnded = (data) => {
      setBattleResult(data);
      setBattle(null);
    };

    const onArenaError = (data) => {
      setMessage(data.message || 'An error occurred.');
    };

    socket.on('arena_state', onArenaState);
    socket.on('arena_lobby_update', onLobbyUpdate);
    socket.on('arena_challenge_created', onChallengeCreated);
    socket.on('arena_challenge_cancelled', onChallengeCancelled);
    socket.on('arena_battle_started', onBattleStarted);
    socket.on('arena_battle_update', onBattleUpdate);
    socket.on('arena_battle_ended', onBattleEnded);
    socket.on('arena_error', onArenaError);

    return () => {
      socket.off('arena_state', onArenaState);
      socket.off('arena_lobby_update', onLobbyUpdate);
      socket.off('arena_challenge_created', onChallengeCreated);
      socket.off('arena_challenge_cancelled', onChallengeCancelled);
      socket.off('arena_battle_started', onBattleStarted);
      socket.off('arena_battle_update', onBattleUpdate);
      socket.off('arena_battle_ended', onBattleEnded);
      socket.off('arena_error', onArenaError);
    };
  }, [socket]);

  // Auto-scroll battle log
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [battle?.log]);

  // Clear status messages after 4 seconds
  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(''), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  // Lobby actions 
  const createChallenge = useCallback((targetId, targetName) => {
    if (!socket) return;
    socket.emit('arena_challenge', { targetId: targetId || null, targetName: targetName || null });
  }, [socket]);

  const cancelChallenge = useCallback(() => {
    if (!socket) return;
    socket.emit('arena_cancel_challenge');
  }, [socket]);

  const acceptChallenge = useCallback((challengeId) => {
    if (!socket) return;
    socket.emit('arena_accept', { challengeId });
  }, [socket]);

  // ⚔️ Battle actions 
  const sendAction = useCallback((action) => {
    if (!socket) return;
    socket.emit('arena_action', action);
  }, [socket]);

  // Deck-mode card actions
  const handlePlayCard = useCallback((cardId) => {
    if (!socket) return;
    socket.emit('arena_action', { type: 'play_card', cardId });
  }, [socket]);

  const handleDeckEndTurn = useCallback(() => {
    if (!socket) return;
    socket.emit('arena_action', { type: 'end_turn' });
  }, [socket]);

  const handleScryResolve = useCallback((topIds, bottomIds) => {
    if (!socket) return;
    socket.emit('arena_action', { type: 'scry_resolve', topIds, bottomIds });
  }, [socket]);

  const forfeit = useCallback(() => {
    if (!socket) return;
    if (window.confirm('Are you sure you want to forfeit? This counts as a loss.')) {
      socket.emit('arena_forfeit');
    }
  }, [socket]);

  const handleCloseResult = () => {
    setBattleResult(null);
    setMode('lobby');
    // Refresh arena state
    if (socket) socket.emit('arena_enter');
  };

  // Keyboard handler for battle 
  useEffect(() => {
    if (mode !== 'battle' || !battle) return;

    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (!battle.yourTurn) return;

      const key = e.key.toLowerCase();
      if (actionMode === 'main') {
        if (key === 'a' || key === '1') { sendAction({ type: 'attack' }); }
        else if (key === 's' || key === '2') { setActionMode('spells'); }
        else if (key === 'd' || key === '3') { setActionMode('abilities'); }
        else if (key === 'i' || key === '4') { setActionMode('items'); }
        else if (key === 'q') { setActionMode('main'); }
      } else if (actionMode === 'spells' || actionMode === 'abilities' || actionMode === 'items') {
        if (key === 'q' || key === 'escape') { setActionMode('main'); }
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mode, battle, actionMode, sendAction]);

  // 
  // RENDER: BATTLE RESULT
  // 
  if (battleResult) {
    const isVictory = battleResult.result === 'victory';
    const isDraw = battleResult.result === 'draw';
    return (
      <div className="arena-overlay">
        <div className="arena-modal arena-result-modal">
          <div className={`arena-result-banner ${battleResult.result}`}>
            {isVictory ? '✨ VICTORY!' : isDraw ? '✨ DRAW!' : '✨ DEFEAT'}
          </div>
          <p className="arena-result-message">{battleResult.message}</p>
          {battleResult.xpGain > 0 && (
            <p className="arena-xp-gain">+{battleResult.xpGain} XP</p>
          )}
          <div className="arena-result-log" ref={logRef}>
            {(battleResult.log || []).map((entry, i) => (
              <div key={i} className="arena-log-entry">{entry}</div>
            ))}
          </div>
          <button className="arena-btn arena-btn-primary" onClick={handleCloseResult}>
            Return to Arena
          </button>
        </div>
      </div>
    );
  }

  // 
  // RENDER: BATTLE
  // 
  if (mode === 'battle' && battle) {
    const you = battle.you;
    const opp = battle.opponent;
    const yourTurn = battle.yourTurn;

    const yourHpPct = Math.max(0, Math.round((you.currentHp / you.hp) * 100));
    const oppHpPct = Math.max(0, Math.round((opp.currentHp / opp.hp) * 100));
    const yourMpPct = you.mp > 0 ? Math.max(0, Math.round((you.currentMp / you.mp) * 100)) : 0;

    // Turn timer
    const timeLeft = Math.max(0, Math.floor((battle.turnTimer - Date.now()) / 1000));

    return (
      <div className="arena-overlay">
        <div className="arena-modal arena-battle-modal">
          {/* Header */}
          <div className="arena-battle-header">
            <span className="arena-round">Round {battle.round}</span>
            <span className="arena-title">ARENA BATTLE </span>
            <span className="arena-timer">{timeLeft}s</span>
          </div>

          <div className="arena-battle-body">
            {/* Left: Your Fighter */}
            <div className={`arena-fighter arena-fighter-you ${yourTurn ? 'active-turn' : ''}`}>
              <div className="arena-fighter-name">{you.name}</div>
              <div className="arena-fighter-info">Lv{you.level} {you.class}</div>
              <div className="arena-hp-bar-container">
                <div className="arena-hp-bar" style={{ width: `${yourHpPct}%` }} />
                <span className="arena-hp-text">{you.currentHp} / {you.hp}</span>
              </div>
              {you.mp > 0 && (
                <div className="arena-mp-bar-container">
                  <div className="arena-mp-bar" style={{ width: `${yourMpPct}%` }} />
                  <span className="arena-mp-text">{you.currentMp} / {you.mp}</span>
                </div>
              )}
              {/* Deck Block indicator */}
              {you.deck?.block > 0 && (
                <div className="arena-block-indicator">
                  🛡️ Block: {you.deck.block}
                </div>
              )}
              <div className="arena-fighter-stats">
                ATT: {you.att + (you.buffs?.filter(b => b.type === 'att').reduce((s, b) => s + b.value, 0) || 0)} |
                DEF: {you.def + (you.buffs?.filter(b => b.type === 'def').reduce((s, b) => s + b.value, 0) || 0)} |
                DR: {you.dr || 0}
              </div>
              {/* Buffs/Debuffs */}
              <div className="arena-effects">
                {(you.buffs || []).map((b, i) => (
                  <span key={`b${i}`} className="arena-buff" title={`${b.name}: +${b.value} ${b.type} (${b.rounds}r)`}>
                    {b.type.toUpperCase()}
                  </span>
                ))}
                {(you.debuffs || []).map((d, i) => (
                  <span key={`d${i}`} className="arena-debuff" title={`${d.name}: -${d.value} ${d.type} (${d.rounds}r)`}>
                    {d.type.toUpperCase()}
                  </span>
                ))}
                {you.mirrorImages > 0 && <span className="arena-buff" title="Mirror Images">{you.mirrorImages}</span>}
                {you.manaFountainActive && <span className="arena-buff" title="Mana Fountain">🔵 MP</span>}
                {you.thirdEyeActive && <span className="arena-buff" title="Third Eye">2</span>}
                {you.boostActive && <span className="arena-buff" title="Boost">CRIT</span>}
              </div>
            </div>

            {/* Center: Battle Log */}
            <div className="arena-battle-log" ref={logRef}>
              {(battle.log || []).map((entry, i) => {
                let cls = 'arena-log-entry';
                if (entry.includes('') || entry.includes('Round')) cls += ' arena-log-header';
                else if (entry.includes('damage')) cls += ' arena-log-damage';
                else if (entry.includes('heal') || entry.includes('Heals')) cls += ' arena-log-heal';
                else if (entry.includes('miss') || entry.includes('resists')) cls += ' arena-log-miss';
                else if (entry.includes('') || entry.includes('')) cls += ' arena-log-finish';
                else if (entry.includes('turn!')) cls += ' arena-log-turn';
                return <div key={i} className={cls}>{entry}</div>;
              })}
            </div>

            {/* Right: Opponent */}
            <div className={`arena-fighter arena-fighter-opp ${!yourTurn ? 'active-turn' : ''}`}>
              <div className="arena-fighter-name">{opp.name}</div>
              <div className="arena-fighter-info">Lv{opp.level} {opp.class}</div>
              <div className="arena-hp-bar-container">
                <div className="arena-hp-bar arena-hp-bar-enemy" style={{ width: `${oppHpPct}%` }} />
                <span className="arena-hp-text">{opp.currentHp} / {opp.hp}</span>
              </div>
              <div className="arena-fighter-stats">
                ATT: {opp.att} | DEF: {opp.def} | DR: {opp.dr || 0}
              </div>
              <div className="arena-effects">
                {(opp.buffs || []).map((b, i) => (
                  <span key={`b${i}`} className="arena-buff" title={`${b.name}: +${b.value} ${b.type} (${b.rounds}r)`}>
                    {b.type.toUpperCase()}
                  </span>
                ))}
                {(opp.debuffs || []).map((d, i) => (
                  <span key={`d${i}`} className="arena-debuff" title={`${d.name}: -${d.value} ${d.type} (${d.rounds}r)`}>
                    {d.type.toUpperCase()}
                  </span>
                ))}
                {opp.mirrorImages > 0 && <span className="arena-buff" title="Mirror Images">{opp.mirrorImages}</span>}
              </div>
            </div>
          </div>

          {/* Turn Indicator */}
          <div className={`arena-turn-indicator ${yourTurn ? 'your-turn' : 'their-turn'}`}>
            {yourTurn ? '✨ YOUR TURN  Choose an action!' : `⏳ Waiting for ${opp.name}...`}
          </div>

          {/* Actions Panel */}
          {yourTurn && (
            <div className="arena-actions">
              {/* ── DECK MODE: Card Hand replaces classic action buttons ── */}
              {you.deck && actionMode === 'main' && (
                <div className="arena-deck-panel">
                  <CardHand
                    deckState={you.deck}
                    character={character}
                    onPlayCard={handlePlayCard}
                    onEndTurn={handleDeckEndTurn}
                    onScryResolve={handleScryResolve}
                    isPlayerTurn={yourTurn}
                  />
                  <div className="arena-deck-utility">
                    {you.usableItems?.length > 0 && (
                      <button className="arena-btn arena-btn-items" onClick={() => setActionMode('items')}>
                        🎒 Items ({you.usableItems.length})
                      </button>
                    )}
                    <button className="arena-btn arena-btn-forfeit" onClick={forfeit}>
                      🏳️ Forfeit
                    </button>
                  </div>
                </div>
              )}

              {/* ── CLASSIC MODE: Original action buttons (when no deck system) ── */}
              {!you.deck && actionMode === 'main' && (
                <div className="arena-action-buttons">
                  <button className="arena-btn arena-btn-attack" onClick={() => sendAction({ type: 'attack' })}>
                    <kbd>A</kbd>  Attack
                  </button>
                  {you.availableSpells?.length > 0 && (
                    <button className="arena-btn arena-btn-spells" onClick={() => setActionMode('spells')}>
                      <kbd>S</kbd>  Spells ({you.availableSpells.length})
                    </button>
                  )}
                  {you.availableAbilities?.length > 0 && (
                    <button className="arena-btn arena-btn-abilities" onClick={() => setActionMode('abilities')}>
                      <kbd>D</kbd>  Abilities ({you.availableAbilities.length})
                    </button>
                  )}
                  {you.usableItems?.length > 0 && (
                    <button className="arena-btn arena-btn-items" onClick={() => setActionMode('items')}>
                      <kbd>I</kbd>  Items ({you.usableItems.length})
                    </button>
                  )}
                  <button className="arena-btn arena-btn-forfeit" onClick={forfeit}>
                    🏳️ Forfeit
                  </button>
                </div>
              )}

              {actionMode === 'spells' && (
                <div className="arena-sub-panel">
                  <div className="arena-sub-header">
                    <span>Spells</span>
                    <button className="arena-btn-back" onClick={() => setActionMode('main')}>⬅️ Back (Q)</button>
                  </div>
                  <div className="arena-spell-list">
                    {(you.availableSpells || []).map((spell, i) => {
                      const info = SPELL_TYPE_INFO[spell.type] || { icon: '✨', color: '#999' };
                      const canAfford = you.currentMp >= (spell.mp || 0) || you.manaFountainActive;
                      return (
                        <button
                          key={i}
                          className={`arena-spell-btn ${canAfford ? '' : 'disabled'}`}
                          disabled={!canAfford}
                          style={{ borderColor: info.color }}
                          onClick={() => canAfford && sendAction({ type: 'spell', spell: spell.name })}
                          title={`${spell.name}  ${spell.mp} MP`}
                        >
                          <span className="arena-spell-icon">{info.icon}</span>
                          <span className="arena-spell-name">{spell.name}</span>
                          <span className="arena-spell-cost">{spell.mp} MP</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {actionMode === 'abilities' && (
                <div className="arena-sub-panel">
                  <div className="arena-sub-header">
                    <span>Abilities</span>
                    <button className="arena-btn-back" onClick={() => setActionMode('main')}>⬅️ Back (Q)</button>
                  </div>
                  <div className="arena-ability-list">
                    {(you.availableAbilities || []).map((ab, i) => {
                      const canAfford = you.currentMp >= (ab.mp || 0);
                      return (
                        <button
                          key={i}
                          className={`arena-ability-btn ${canAfford ? '' : 'disabled'}`}
                          disabled={!canAfford}
                          onClick={() => canAfford && sendAction({ type: 'ability', ability: ab.name })}
                          title={ab.description || ab.name}
                        >
                          <span className="arena-ability-name">{ab.name}</span>
                          {ab.mp > 0 && <span className="arena-ability-cost">{ab.mp} MP</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {actionMode === 'items' && (
                <div className="arena-sub-panel">
                  <div className="arena-sub-header">
                    <span>Items</span>
                    <button className="arena-btn-back" onClick={() => setActionMode('main')}>⬅️ Back (Q)</button>
                  </div>
                  <div className="arena-item-list">
                    {(you.usableItems || []).map((item, i) => {
                      const apCost = you.deck ? (item.apCost || 1) : 0;
                      const unaffordable = apCost > 0 && (you.deck?.ap ?? 0) < apCost;
                      return (
                        <button
                          key={i}
                          className={`arena-item-btn ${unaffordable ? 'unaffordable' : ''}`}
                          disabled={unaffordable}
                          onClick={() => sendAction({ type: 'item', item: item.name })}
                        >
                          <span className="arena-item-name">{item.name}</span>
                          {apCost > 0 && <span className="item-ap-cost">{apCost} AP</span>}
                          {item.quantity > 1 && <span className="arena-item-qty">x{item.quantity}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // 
  // RENDER: LOBBY
  // 
  const myCharId = character?.id;
  const currentHp = (character?.max_hp || 20) - (character?.damage || 0);
  const currentHpPct = Math.round((currentHp / (character?.max_hp || 20)) * 100);
  const canChallenge = currentHpPct >= 50;

  return (
    <div className="arena-overlay">
      <div className="arena-modal arena-lobby-modal">
        {/* Header */}
        <div className="arena-lobby-header">
          <h2>The Colosseum</h2>
          <button className="arena-close-btn" onClick={onClose}>✖</button>
        </div>

        {/* Status message */}
        {message && <div className="arena-message">{message}</div>}

        {/* My Record */}
        <div className="arena-my-record">
          <span className="arena-record-name">{character?.name}</span>
          <span className="arena-record-stat wins">W: {myRecord.wins}</span>
          <span className="arena-record-stat losses">L: {myRecord.losses}</span>
          <span className="arena-record-stat draws">D: {myRecord.draws}</span>
          <span className="arena-record-hp" style={{ color: canChallenge ? '#4ade80' : '#ef4444' }}>
            HP: {currentHp}/{character?.max_hp || 20}
          </span>
        </div>

        {/* Challenge Button */}
        <div className="arena-challenge-area">
          {myChallengeOpen ? (
            <button className="arena-btn arena-btn-cancel" onClick={cancelChallenge}>
              ❌ Cancel Challenge
            </button>
          ) : (
            <button
              className="arena-btn arena-btn-primary"
              disabled={!canChallenge}
              onClick={() => createChallenge()}
              title={!canChallenge ? 'Need at least 50% HP to challenge' : 'Post an open challenge'}
            >
               Post Open Challenge
            </button>
          )}
          {!canChallenge && (
            <span className="arena-hp-warning">Need 50% HP to fight</span>
          )}
        </div>

        {/* Tabs */}
        <div className="arena-tabs">
          <button className={`arena-tab ${tab === 'challenges' ? 'active' : ''}`} onClick={() => setTab('challenges')}>
            ⚔️ Challenges ({challenges.length})
          </button>
          <button className={`arena-tab ${tab === 'active' ? 'active' : ''}`} onClick={() => setTab('active')}>
             Active Fights ({activeFights.length})
          </button>
          <button className={`arena-tab ${tab === 'leaderboard' ? 'active' : ''}`} onClick={() => setTab('leaderboard')}>
             Leaderboard
          </button>
        </div>

        {/* Tab Content */}
        <div className="arena-tab-content">
          {/* Challenges Tab */}
          {tab === 'challenges' && (
            <div className="arena-challenges">
              {challenges.length === 0 ? (
                <div className="arena-empty">No open challenges. Be the first to throw down the gauntlet!</div>
              ) : (
                challenges.map(ch => (
                  <div key={ch.id} className="arena-challenge-row">
                    <div className="arena-challenge-info">
                      <span className="arena-ch-name">{ch.challengerName}</span>
                      <span className="arena-ch-detail">Lv{ch.challengerLevel} {ch.challengerClass}</span>
                      {ch.targetName && (
                        <span className="arena-ch-target">vs {ch.targetName}</span>
                      )}
                    </div>
                    {ch.challengerId !== myCharId && (
                      <button
                        className="arena-btn arena-btn-accept"
                        onClick={() => acceptChallenge(ch.id)}
                        disabled={!canChallenge || (ch.targetId && ch.targetId !== myCharId)}
                      >
                        Accept </button>
                    )}
                    {ch.challengerId === myCharId && (
                      <span className="arena-ch-yours">Your Challenge</span>
                    )}
                  </div>
                ))
              )}

              {/* Online Players  direct challenge */}
              {onlinePlayers.length > 0 && (
                <div className="arena-online-section">
                  <h4>Online Players</h4>
                  {onlinePlayers.map(p => (
                    <div key={p.id} className="arena-online-row">
                      <span className="arena-online-name">{p.name}</span>
                      <button
                        className="arena-btn arena-btn-sm"
                        disabled={!canChallenge || myChallengeOpen}
                        onClick={() => createChallenge(p.id, p.name)}
                      >
                        Challenge
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Active Fights Tab */}
          {tab === 'active' && (
            <div className="arena-active-fights">
              {activeFights.length === 0 ? (
                <div className="arena-empty">No battles in progress.</div>
              ) : (
                activeFights.map(f => (
                  <div key={f.id} className="arena-fight-row">
                    <span className="arena-fight-p1">{f.player1} (Lv{f.player1Level} {f.player1Class})</span>
                    <span className="arena-fight-vs">vs </span>
                    <span className="arena-fight-p2">{f.player2} (Lv{f.player2Level} {f.player2Class})</span>
                    <span className="arena-fight-round">Rd {f.round}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Leaderboard Tab */}
          {tab === 'leaderboard' && (
            <div className="arena-leaderboard">
              {leaderboard.length === 0 ? (
                <div className="arena-empty">No arena records yet. Be the first to fight!</div>
              ) : (
                <table className="arena-lb-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Class</th>
                      <th>Lv</th>
                      <th>W</th>
                      <th>L</th>
                      <th>D</th>
                      <th>Win %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((row, i) => {
                      const total = row.wins + row.losses + row.draws;
                      const winPct = total > 0 ? Math.round((row.wins / total) * 100) : 0;
                      return (
                        <tr key={i} className={i < 3 ? `arena-lb-top${i + 1}` : ''}>
                          <td>{i + 1}</td>
                          <td>{row.name}</td>
                          <td>{row.class}</td>
                          <td>{row.level}</td>
                          <td className="arena-lb-wins">{row.wins}</td>
                          <td className="arena-lb-losses">{row.losses}</td>
                          <td className="arena-lb-draws">{row.draws}</td>
                          <td>{winPct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ArenaModal;
